import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  TextButtonRegularSmall as TextButton,
  PrimaryButtonMedium as PrimaryButton,
  SecondaryButtonMedium as SecondaryButton,
} from '@xingternal/malt-button';
import UserImage from '@xingternal/malt-user-image';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';
import { H } from '@xingternal/malt-typography';

import StretchButtonGroup from './../../Components/StretchButtonGroup/StretchButtonGroup';
import PasswordField from './../../Components/PasswordField/PasswordField';
import FormMessage from './../../Components/FormMessage/FormMessage';
import CenterImage from './../../Components/CenterImage/CenterImage';
import { CustomH, CustomP } from './../../Components/CustomMalt/Typography';
import Experiment from './../../Components/Experiment/Experiment';
import Spacer from './../../Components/Spacer/Spacer';

import styles from './LoginKnownUser.css';
import messages from './messages';

const getSalutationKey = gender =>
  gender === 'm' ? messages.salutationMale : messages.salutationFemale;

const TestableForm = testable(QA_MALT, 'form');

const LoginKnownUser = ({
  handleChange,
  handleSubmit,
  oldLoginAction,
  forgotPasswordAction,
  oneTimeLinkAction,
  isSubmitting,
  errors,
  passwordRef,
  user: { email, lastName, firstName, gender, imageUrl },
  formMessage: { notice, error },
  oneTimeLinkError,
  didResetPassword,
  setErrors,
  selectedVariantName,
}) => {
  useEffect(() => {
    if (error) {
      setErrors({ credentials: error });
    }
  }, []);

  return (
    <>
      <CenterImage
        image={
          <UserImage
            userName={email}
            src={`${imageUrl}.64x64.jpg`}
            size={'300'}
            gender={gender}
            className={styles.userImage}
          />
        }
      />
      <CustomH
        testId={'login-known-user-headline'}
        className={styles.userSalutation}
      >
        {didResetPassword ? (
          <FormattedMessage {...messages.postRecoveryTitle} />
        ) : (
          <>
            <FormattedMessage {...messages.salutationText} />
            <br />
            <FormattedMessage
              {...getSalutationKey(gender)}
              values={{
                lastName,
                firstName,
              }}
            />
          </>
        )}
      </CustomH>
      <TestableForm
        onSubmit={handleSubmit}
        method={'POST'}
        testId={'login-known-user-form'}
      >
        <PasswordField
          // To be updated once the AB test winner is implemented
          autoFocus={selectedVariantName === 'a'}
          label={<FormattedMessage {...messages.passwordLabel} />}
          inputRef={passwordRef}
          handleChange={handleChange}
        />

        <div className={styles.inputActions}>
          <TextButton
            onClick={() => forgotPasswordAction(email)}
            noPadding
            testId={'login-forgot-password-link'}
            type={'button'}
          >
            <FormattedMessage {...messages.forgotPasswordLabel} />
          </TextButton>
        </div>

        <FormMessage notice={notice} error={errors.credentials} />

        <StretchButtonGroup>
          <PrimaryButton
            testId={'login-known-user-submit'}
            type={'submit'}
            loading={isSubmitting}
          >
            <FormattedMessage {...messages.buttonLabel} />
          </PrimaryButton>
          <TextButton
            onClick={oldLoginAction}
            noPadding
            testId={'login-button-not-me'}
          >
            <FormattedMessage {...messages.notYouLink} />
          </TextButton>
        </StretchButtonGroup>
        <Experiment
          selectedVariantName={selectedVariantName}
          variants={{
            a: null,
            b: (
              <>
                <Spacer size={6} />
                <div className={styles.alignCenterContainer}>
                  <H level={4}>
                    <FormattedMessage {...messages.oneTimeLinkTeaser} />
                  </H>
                  <CustomP>
                    <FormattedMessage {...messages.oneTimeLinkExplainer} />
                  </CustomP>
                  <Spacer size={2} />
                  {oneTimeLinkError && (
                    <FormMessage
                      error={
                        <FormattedMessage
                          {...messages.oneTimeLinkLimitReached}
                        />
                      }
                    />
                  )}

                  <SecondaryButton onClick={oneTimeLinkAction}>
                    <FormattedMessage {...messages.oneTimeLinkLink} />
                  </SecondaryButton>
                </div>

                <Spacer size={6} />
              </>
            ),
            c: (
              <>
                <Spacer
                  size={6}
                  text={<FormattedMessage {...messages.orOneTimeLink} />}
                />

                <div className={styles.alignCenterContainer}>
                  <H level={4}>
                    <FormattedMessage {...messages.oneTimeLinkTeaser} />
                  </H>
                  <Spacer size={2} />

                  {oneTimeLinkError && (
                    <FormMessage
                      error={
                        <FormattedMessage
                          {...messages.oneTimeLinkLimitReached}
                        />
                      }
                    />
                  )}

                  <SecondaryButton onClick={oneTimeLinkAction}>
                    <FormattedMessage {...messages.oneTimeLinkLink} />
                  </SecondaryButton>
                  <Spacer size={4} />
                  <CustomP>
                    <FormattedMessage {...messages.oneTimeLinkExplainer} />
                  </CustomP>
                </div>

                <Spacer size={6} />
              </>
            ),
          }}
        />
      </TestableForm>
    </>
  );
};

LoginKnownUser.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  oldLoginAction: PropTypes.func.isRequired,
  oneTimeLinkAction: PropTypes.func.isRequired,
  forgotPasswordAction: PropTypes.func.isRequired,
  formMessage: PropTypes.object.isRequired,
  oneTimeLinkError: PropTypes.string,
  passwordRef: PropTypes.object,
  isSubmitting: PropTypes.bool,
  user: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    gender: PropTypes.string,
    imageUrl: PropTypes.string,
    hashedId: PropTypes.integer,
  }),
  errors: PropTypes.object,
  values: PropTypes.object.isRequired,
  didResetPassword: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  selectedVariantName: PropTypes.string,
};

export default injectIntl(LoginKnownUser);
